import React, { useEffect, useMemo, useState } from 'react';
import { Link, Navigate, useNavigate, useParams } from 'react-router-dom';
import { supabase } from '../supabaseClient';
import { useAuth } from '../context/AuthContext';
import { generateSlug, SLUG_REGEX, UUID_REGEX } from '../utils/slug';

type ClubFormState = {
  name: string;
  slug: string;
  description: string;
  location: string;
  latitude: string;
  longitude: string;
  hero_image: string;
  logo: string;
  instagram_handle: string;
  facebook_link: string;
  strava_link: string;
};

type RunFormRow = {
  id?: string;
  title: string;
  is_recurring: boolean;
  day_of_week: string;
  run_date: string; // YYYY-MM-DD, only used when is_recurring = false
  start_time: string;
  location_address: string;
  distance: string;
  vibes: string[];
  notes: string;
  route_url: string;
  is_active: boolean;
};

type RunDbPayload = {
  id?: string;
  club_id?: string;
  title: string | null;
  is_recurring: boolean;
  day_of_week: string;
  run_date: string | null;
  start_time: string | null;
  location_address: string | null;
  distance: string | null;
  vibes: string[];
  notes: string | null;
  route_url: string | null;
  is_active: boolean;
};

type ClubRecord = {
  id: string;
  slug: string;
  name: string;
  description?: string | null;
  location?: string | null;
  latitude?: number | null;
  longitude?: number | null;
  hero_image?: string | null;
  logo?: string | null;
  instagram_handle?: string | null;
  facebook_link?: string | null;
  strava_link?: string | null;
  leader_id?: string | null;
};

type RunRecord = {
  id: string;
  club_id: string;
  title?: string | null;
  is_recurring?: boolean | null;
  day_of_week: string;
  run_date?: string | null;
  start_time?: string | null;
  location_address?: string | null;
  distance?: string | null;
  vibes?: string[] | null;
  notes?: string | null;
  route_url?: string | null;
  is_active?: boolean | null;
};

const STORAGE_BUCKET = 'club-assets';

const DAY_OPTIONS = [
  'Monday',
  'Tuesday',
  'Wednesday',
  'Thursday',
  'Friday',
  'Saturday',
  'Sunday',
];

const VIBE_OPTIONS = [
  'Social',
  'Beginner-Friendly',
  'Fast',
  'Competitive',
  'Long Run',
  'Trail',
  'Workout',
  'Recovery',
  'Women-Led',
  'Dog-Friendly',
];

const emptyClubForm: ClubFormState = {
  name: '',
  slug: '',
  description: '',
  location: '',
  latitude: '',
  longitude: '',
  hero_image: '',
  logo: '',
  instagram_handle: '',
  facebook_link: '',
  strava_link: '',
};

function dayOfWeekFromDate(dateStr: string): string {
  if (!dateStr) return 'Saturday';
  const d = new Date(dateStr + 'T00:00:00');
  return DAY_OPTIONS[(d.getDay() + 6) % 7]; // Mon=0…Sun=6
}

function makeEmptyRun(day = 'Saturday'): RunFormRow {
  return {
    title: '',
    is_recurring: true,
    day_of_week: day,
    run_date: '',
    start_time: '',
    location_address: '',
    distance: '',
    vibes: [],
    notes: '',
    route_url: '',
    is_active: true,
  };
}

function normalizeClubForm(club: ClubRecord): ClubFormState {
  return {
    name: club.name || '',
    slug: club.slug || '',
    description: club.description || '',
    location: club.location || '',
    latitude:
      club.latitude !== null && club.latitude !== undefined
        ? String(club.latitude)
        : '',
    longitude:
      club.longitude !== null && club.longitude !== undefined
        ? String(club.longitude)
        : '',
    hero_image: club.hero_image || '',
    logo: club.logo || '',
    instagram_handle: club.instagram_handle || '',
    facebook_link: club.facebook_link || '',
    strava_link: club.strava_link || '',
  };
}

function normalizeRunForm(run: RunRecord): RunFormRow {
  return {
    id: run.id,
    title: run.title || '',
    is_recurring: run.is_recurring !== false,
    day_of_week: run.day_of_week || 'Saturday',
    run_date: run.run_date || '',
    start_time: run.start_time || '',
    location_address: run.location_address || '',
    distance: run.distance || '',
    vibes: run.vibes || [],
    notes: run.notes || '',
    route_url: run.route_url || '',
    is_active: run.is_active !== false,
  };
}

function parseNullableNumber(value: string) {
  const trimmed = value.trim();
  if (!trimmed) return null;
  const parsed = Number(trimmed);
  return Number.isNaN(parsed) ? null : parsed;
}

function sortRuns<T extends { day_of_week: string; start_time?: string | null }>(rows: T[]) {
  return [...rows].sort((a, b) => {
    const dayDiff =
      DAY_OPTIONS.indexOf(a.day_of_week) - DAY_OPTIONS.indexOf(b.day_of_week);

    if (dayDiff !== 0) return dayDiff;
    return (a.start_time || '').localeCompare(b.start_time || '');
  });
}

function formatTimeForDisplay(value: string) {
  if (!value) return '';
  const [hoursString, minutes = '00'] = value.split(':');
  const hours = Number(hoursString);

  if (Number.isNaN(hours)) return value;

  const suffix = hours >= 12 ? 'PM' : 'AM';
  const displayHours = hours % 12 === 0 ? 12 : hours % 12;
  return `${displayHours}:${minutes} ${suffix}`;
}

function sanitizeFileName(name: string) {
  return name.replace(/[^a-zA-Z0-9.\-_]/g, '-');
}

async function geocodeAddress(address: string) {
  const query = address.trim();
  if (!query) {
    throw new Error('Please enter an address first.');
  }

  const response = await fetch(
    `https://nominatim.openstreetmap.org/search?format=jsonv2&limit=1&q=${encodeURIComponent(
      query
    )}`,
    {
      headers: {
        Accept: 'application/json',
      },
    }
  );

  if (!response.ok) {
    throw new Error('Unable to verify address right now.');
  }

  const data = (await response.json()) as Array<{
    lat: string;
    lon: string;
    display_name: string;
  }>;

  if (!data.length) {
    throw new Error('Address not found. Try a more specific address.');
  }

  return {
    latitude: data[0].lat,
    longitude: data[0].lon,
    display_name: data[0].display_name,
  };
}

async function uploadClubImage(file: File, folder: 'logos' | 'hero-images') {
  const extension = file.name.split('.').pop() || 'jpg';
  const safeName = sanitizeFileName(file.name.replace(/\.[^.]+$/, ''));
  const filePath = `${folder}/${Date.now()}-${safeName}.${extension}`;

  const { error: uploadError } = await supabase.storage
    .from(STORAGE_BUCKET)
    .upload(filePath, file, {
      cacheControl: '3600',
      upsert: false,
    });

  if (uploadError) {
    throw uploadError;
  }

  const { data } = supabase.storage.from(STORAGE_BUCKET).getPublicUrl(filePath);
  return data.publicUrl;
}

function Input({
  label,
  value,
  onChange,
  placeholder,
  multiline = false,
  type = 'text',
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  multiline?: boolean;
  type?: React.InputHTMLAttributes<HTMLInputElement>['type'];
}) {
  return (
    <label className="block">
      <span className="mb-2 block text-sm font-medium text-black">{label}</span>
      {multiline ? (
        <textarea
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          rows={5}
          className="w-full rounded-2xl border border-black/10 bg-white px-4 py-3 text-sm text-black outline-none transition focus:border-black"
        />
      ) : (
        <input
          type={type}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          className="w-full rounded-2xl border border-black/10 bg-white px-4 py-3 text-sm text-black outline-none transition focus:border-black"
        />
      )}
    </label>
  );
}

function ImageUploadField({
  label,
  value,
  previewHeightClass,
  onUpload,
  uploading,
}: {
  label: string;
  value: string;
  previewHeightClass: string;
  onUpload: (file: File) => Promise<void>;
  uploading: boolean;
}) {
  return (
    <div className="block">
      <span className="mb-2 block text-sm font-medium text-black">{label}</span>

      <div className="rounded-[1.5rem] border border-black/10 bg-white p-4">
        <div
          className={`overflow-hidden rounded-[1.25rem] border border-black/10 bg-neutral-50 ${previewHeightClass}`}
        >
          {value ? (
            <img src={value} alt={label} className="h-full w-full object-cover" />
          ) : (
            <div className="flex h-full w-full items-center justify-center text-sm text-black/45">
              No image uploaded yet
            </div>
          )}
        </div>

        <div className="mt-4">
          <label className="inline-flex cursor-pointer rounded-2xl bg-black px-4 py-2.5 text-sm font-medium text-white transition hover:opacity-90">
            {uploading ? 'Uploading...' : 'Upload image'}
            <input
              type="file"
              accept="image/*"
              className="hidden"
              disabled={uploading}
              onChange={async (e) => {
                const file = e.target.files?.[0];
                e.currentTarget.value = '';
                if (!file) return;
                await onUpload(file);
              }}
            />
          </label>
        </div>

        {value ? (
          <p className="mt-3 break-all text-xs text-black/45">{value}</p>
        ) : null}
      </div>
    </div>
  );
}

export default function CreateClub() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user, leader, canManageClub, loading: authLoading } = useAuth();

  const isEditMode = Boolean(id);

  const [clubForm, setClubForm] = useState<ClubFormState>(emptyClubForm);
  const [runs, setRuns] = useState<RunFormRow[]>([makeEmptyRun()]);
  const [initialRunIds, setInitialRunIds] = useState<string[]>([]);
  const [clubRecord, setClubRecord] = useState<ClubRecord | null>(null);

  const [loading, setLoading] = useState(isEditMode);
  const [saving, setSaving] = useState(false);
  const [pageError, setPageError] = useState('');
  const [saveMessage, setSaveMessage] = useState('');

  const [slugError, setSlugError] = useState('');
  const [slugChecking, setSlugChecking] = useState(false);

  const [addressLookupLoading, setAddressLookupLoading] = useState(false);
  const [addressLookupMessage, setAddressLookupMessage] = useState('');

  const [logoUploading, setLogoUploading] = useState(false);
  const [heroUploading, setHeroUploading] = useState(false);

  useEffect(() => {
    async function loadEditData() {
      if (!id) {
        setLoading(false);
        return;
      }

      setLoading(true);
      setPageError('');

      // Support both UUID (legacy) and slug-based edit URLs
      const isUuid = UUID_REGEX.test(id);
      const { data: clubData, error: clubError } = await (
        isUuid
          ? supabase.from('clubs').select('*').eq('id', id).single()
          : supabase.from('clubs').select('*').eq('slug', id).single()
      );

      const clubUuid = (clubData as ClubRecord | null)?.id ?? id;

      const [{ data: runsData, error: runsError }] =
        await Promise.all([
          supabase
            .from('runs')
            .select('*')
            .eq('club_id', clubUuid)
            .order('day_of_week', { ascending: true })
            .order('start_time', { ascending: true }),
        ]);

      if (clubError) {
        console.error('Failed to load club for edit:', clubError.message);
        setPageError('Unable to load this club for editing.');
        setLoading(false);
        return;
      }

      if (runsError) {
        console.error('Failed to load runs for edit:', runsError.message);
      }

      const resolvedClub = (clubData as ClubRecord) || null;
      const resolvedRuns = ((runsData || []) as RunRecord[]).filter(
        (run) => run.is_active !== false
      );

      setClubRecord(resolvedClub);
      setClubForm(normalizeClubForm(resolvedClub));
      setRuns(
        resolvedRuns.length ? sortRuns(resolvedRuns.map(normalizeRunForm)) : [makeEmptyRun()]
      );
      setInitialRunIds(resolvedRuns.map((run) => run.id));
      setLoading(false);
    }

    loadEditData();
  }, [id]);

  const canEdit = useMemo(() => {
    if (!isEditMode) return Boolean(leader);
    if (!clubRecord || !leader) return false;
    return canManageClub(clubRecord.id);
  }, [isEditMode, clubRecord, leader, canManageClub]);

  if (authLoading || loading) {
    return (
      <div className="mx-auto max-w-5xl px-4 py-10 sm:px-6">
        <div className="text-sm text-black/60">
          {isEditMode ? 'Loading club editor...' : 'Loading...'}
        </div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/auth" replace />;
  }

  if (!leader) {
    return (
      <div className="mx-auto max-w-3xl px-4 py-10 sm:px-6">
        <div className="rounded-[2rem] border border-black/10 bg-white p-8 shadow-sm">
          <h1 className="text-2xl font-semibold text-black">Leader access required</h1>
          <p className="mt-3 text-sm leading-6 text-black/60">
            You need a run leader profile before you can create or edit a club.
          </p>
        </div>
      </div>
    );
  }

  if (isEditMode && !canEdit) {
    return (
      <div className="mx-auto max-w-3xl px-4 py-10 sm:px-6">
        <div className="rounded-[2rem] border border-black/10 bg-white p-8 shadow-sm">
          <h1 className="text-2xl font-semibold text-black">You can’t edit this club</h1>
          <p className="mt-3 text-sm leading-6 text-black/60">
            Only the assigned club leader or an admin can access the edit workflow.
          </p>
        </div>
      </div>
    );
  }

  function updateClubField<K extends keyof ClubFormState>(key: K, value: ClubFormState[K]) {
    setClubForm((prev) => ({ ...prev, [key]: value }));
  }

  function updateRun(index: number, patch: Partial<RunFormRow>) {
    setRuns((prev) => prev.map((run, i) => (i === index ? { ...run, ...patch } : run)));
  }

  function toggleRunVibe(index: number, vibe: string) {
    setRuns((prev) =>
      prev.map((run, i) => {
        if (i !== index) return run;

        const hasVibe = run.vibes.includes(vibe);
        return {
          ...run,
          vibes: hasVibe
            ? run.vibes.filter((item) => item !== vibe)
            : [...run.vibes, vibe],
        };
      })
    );
  }

  function addRun() {
    setRuns((prev) => [...prev, makeEmptyRun()]);
  }

  function removeRun(index: number) {
    setRuns((prev) => {
      if (prev.length === 1) return [makeEmptyRun()];
      return prev.filter((_, i) => i !== index);
    });
  }

  async function handleAddressLookup() {
    setAddressLookupLoading(true);
    setAddressLookupMessage('');
    setPageError('');

    try {
      const result = await geocodeAddress(clubForm.location);

      setClubForm((prev) => ({
        ...prev,
        latitude: result.latitude,
        longitude: result.longitude,
      }));

      setAddressLookupMessage('Address confirmed and map coordinates added.');
    } catch (error: any) {
      setAddressLookupMessage('');
      setPageError(error?.message || 'Unable to confirm this address.');
    } finally {
      setAddressLookupLoading(false);
    }
  }

  async function handleLogoUpload(file: File) {
    setLogoUploading(true);
    setPageError('');

    try {
      const publicUrl = await uploadClubImage(file, 'logos');
      updateClubField('logo', publicUrl);
    } catch (error: any) {
      console.error('Logo upload failed:', error);
      setPageError(error?.message || 'Logo upload failed.');
    } finally {
      setLogoUploading(false);
    }
  }

  async function handleHeroUpload(file: File) {
    setHeroUploading(true);
    setPageError('');

    try {
      const publicUrl = await uploadClubImage(file, 'hero-images');
      updateClubField('hero_image', publicUrl);
    } catch (error: any) {
      console.error('Hero upload failed:', error);
      setPageError(error?.message || 'Hero image upload failed.');
    } finally {
      setHeroUploading(false);
    }
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setSaving(true);
    setPageError('');
    setSaveMessage('');

    if (!clubForm.name.trim()) {
      setPageError('Club name is required.');
      setSaving(false);
      return;
    }

    if (!clubForm.location.trim()) {
      setPageError('Club address is required.');
      setSaving(false);
      return;
    }

    if (!clubForm.latitude.trim() || !clubForm.longitude.trim()) {
      setPageError('Please confirm the club address before saving.');
      setSaving(false);
      return;
    }

    const unnamedRuns = runs.filter((r) => !r.title.trim());
    if (unnamedRuns.length) {
      setPageError('Each run must have a title. Please name all runs before saving.');
      setSaving(false);
      return;
    }

    const leaderId = leader?.id;

    if (!leaderId) {
      setPageError('Leader profile is required.');
      setSaving(false);
      return;
    }

    const slug = clubForm.slug.trim();
    if (!SLUG_REGEX.test(slug)) {
      setPageError(
        'Club URL must be 3–60 characters and contain only lowercase letters, numbers, and hyphens.'
      );
      setSaving(false);
      return;
    }

    // Check slug uniqueness (exclude current club in edit mode)
    const { data: slugConflict } = await (
      isEditMode && clubRecord?.id
        ? supabase.from('clubs').select('id').eq('slug', slug).neq('id', clubRecord.id).maybeSingle()
        : supabase.from('clubs').select('id').eq('slug', slug).maybeSingle()
    );
    if (slugConflict) {
      setPageError('That club URL is already taken. Please choose a different one.');
      setSaving(false);
      return;
    }

    const clubPayload = {
      name: clubForm.name.trim(),
      slug,
      description: clubForm.description.trim() || null,
      location: clubForm.location.trim() || null,
      latitude: parseNullableNumber(clubForm.latitude),
      longitude: parseNullableNumber(clubForm.longitude),
      hero_image: clubForm.hero_image.trim() || null,
      logo: clubForm.logo.trim() || null,
      instagram_handle: clubForm.instagram_handle.trim().replace(/^@/, '') || null,
      facebook_link: clubForm.facebook_link.trim() || null,
      strava_link: clubForm.strava_link.trim() || null,
      leader_id: isEditMode ? (clubRecord?.leader_id ?? null) : leaderId,
    };

    const sortedRuns = sortRuns(runs);

    const sanitizedRuns: RunDbPayload[] = sortedRuns
      .map((run) => {
        const isRecurring = run.is_recurring;
        const derivedDayOfWeek = isRecurring
          ? run.day_of_week
          : dayOfWeekFromDate(run.run_date);
        return {
          id: run.id,
          title: run.title.trim() || null,
          is_recurring: isRecurring,
          day_of_week: derivedDayOfWeek,
          run_date: isRecurring ? null : run.run_date.trim() || null,
          start_time: run.start_time.trim() || null,
          location_address: run.location_address.trim() || null,
          distance: run.distance.trim() || null,
          notes: run.notes.trim() || null,
          route_url: run.route_url.trim() || null,
          vibes: run.vibes.length ? run.vibes : [],
          is_active: true,
        };
      })
      .filter((run) => {
        if (!run.is_recurring) {
          // One-off: must have a valid date
          return Boolean(run.run_date);
        }
        // Recurring: must have a day and at least one meaningful field
        return (
          run.day_of_week &&
          (run.start_time ||
            run.location_address ||
            run.distance ||
            run.notes ||
            run.route_url ||
            run.vibes.length)
        );
      });

    try {
      let clubUuid = ''; // UUID for run DB operations

      if (isEditMode && clubRecord?.id) {
        const { error: updateError } = await supabase
          .from('clubs')
          .update(clubPayload)
          .eq('id', clubRecord.id);

        if (updateError) throw updateError;
        clubUuid = clubRecord.id;
      } else {
        const { data: insertedClub, error: insertError } = await supabase
          .from('clubs')
          .insert(clubPayload)
          .select('id')
          .single();

        if (insertError) throw insertError;
        clubUuid = insertedClub.id;
      }

      if (clubUuid) {
        const submittedExistingIds = sanitizedRuns
          .map((run) => run.id)
          .filter(Boolean) as string[];

        const removedRunIds = initialRunIds.filter(
          (existingId) => !submittedExistingIds.includes(existingId)
        );

        if (removedRunIds.length) {
          const { error: deactivateError } = await supabase
            .from('runs')
            .update({ is_active: false })
            .in('id', removedRunIds);

          if (deactivateError) throw deactivateError;
        }

        if (sanitizedRuns.length) {
          const existingRuns = sanitizedRuns.filter((run) => run.id);
          const newRuns = sanitizedRuns.filter((run) => !run.id);

          if (existingRuns.length) {
            const { error: upsertError } = await supabase
              .from('runs')
              .upsert(
                existingRuns.map((run) => ({
                  id: run.id,
                  club_id: clubUuid,
                  title: run.title,
                  is_recurring: run.is_recurring,
                  day_of_week: run.day_of_week,
                  run_date: run.run_date,
                  start_time: run.start_time,
                  location_address: run.location_address,
                  distance: run.distance,
                  vibes: run.vibes,
                  notes: run.notes,
                  route_url: run.route_url,
                  is_active: true,
                })),
                { onConflict: 'id' }
              );
            if (upsertError) throw upsertError;
          }

          if (newRuns.length) {
            const { error: insertError } = await supabase
              .from('runs')
              .insert(
                newRuns.map((run) => ({
                  club_id: clubUuid,
                  title: run.title,
                  is_recurring: run.is_recurring,
                  day_of_week: run.day_of_week,
                  run_date: run.run_date,
                  start_time: run.start_time,
                  location_address: run.location_address,
                  distance: run.distance,
                  vibes: run.vibes,
                  notes: run.notes,
                  route_url: run.route_url,
                  is_active: true,
                }))
              );
            if (insertError) throw insertError;
          }
        }

        navigate(`/clubs/${slug}`);
      }
    } catch (error: any) {
      console.error('Failed to save club:', error);
      setPageError(error?.message || 'Something went wrong while saving the club.');
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="mx-auto max-w-5xl px-4 py-8 sm:px-6 md:py-10">
      <div className="space-y-8">
        <section className="rounded-[2rem] border border-black/10 bg-white p-6 shadow-sm md:p-8">
          <p
            className="text-[11px] font-bold uppercase tracking-[0.18em]"
            style={{ color: '#e2a9f1' }}
          >
            {isEditMode ? 'Edit club' : 'Create club'}
          </p>

          <h1 className="mt-2 text-3xl font-bold tracking-tight text-black sm:text-4xl">
            {isEditMode ? 'Update your club' : 'Build your club profile'}
          </h1>

          <p className="mt-3 max-w-2xl text-sm leading-6 text-black/60 sm:text-base">
            {isEditMode
              ? 'Make changes to your club details and weekly run schedule.'
              : 'Add your club details, upload your images, and build your recurring run schedule.'}
          </p>
        </section>

        {pageError ? (
          <div className="rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
            {pageError}
          </div>
        ) : null}

        {saveMessage ? (
          <div className="rounded-2xl border border-green-200 bg-green-50 px-4 py-3 text-sm text-green-700">
            {saveMessage}
          </div>
        ) : null}

        <form onSubmit={handleSubmit} className="space-y-8">
          <section className="rounded-[2rem] border border-black/10 bg-white p-6 shadow-sm md:p-8">
            <h2 className="text-2xl font-semibold tracking-tight text-black">Club details</h2>

            <div className="mt-6 grid gap-5 md:grid-cols-2">
              <div className="md:col-span-2">
                <Input
                  label="Club name"
                  value={clubForm.name}
                  onChange={(value) => {
                    updateClubField('name', value);
                    if (!isEditMode) {
                      updateClubField('slug', generateSlug(value));
                      setSlugError('');
                    }
                  }}
                  placeholder="North County Run Club"
                />
              </div>

              <div className="md:col-span-2">
                <label className="mb-1.5 block text-sm font-medium text-black">Club URL</label>
                <input
                  type="text"
                  value={clubForm.slug}
                  onChange={(e) => {
                    const raw = e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '');
                    updateClubField('slug', raw);
                    setSlugError('');
                  }}
                  onBlur={async () => {
                    const slug = clubForm.slug.trim();
                    if (!slug) return;
                    if (!SLUG_REGEX.test(slug)) {
                      setSlugError(
                        'Slug must be 3–60 characters: lowercase letters, numbers, and hyphens only.'
                      );
                      return;
                    }
                    setSlugChecking(true);
                    const { data } = await (
                      isEditMode && clubRecord?.id
                        ? supabase.from('clubs').select('id').eq('slug', slug).neq('id', clubRecord.id).maybeSingle()
                        : supabase.from('clubs').select('id').eq('slug', slug).maybeSingle()
                    );
                    setSlugChecking(false);
                    setSlugError(data ? 'That URL is already taken.' : '');
                  }}
                  placeholder="north-county-run-club"
                  maxLength={60}
                  className="w-full rounded-2xl border border-black/10 bg-white px-4 py-3 text-sm text-black outline-none transition focus:border-black"
                />
                <p className="mt-1 text-xs text-black/45">
                  runcollective.club/clubs/{clubForm.slug || '…'}
                </p>
                {slugChecking && (
                  <p className="mt-1 text-xs text-black/45">Checking availability…</p>
                )}
                {slugError && (
                  <p className="mt-1 text-xs text-red-600">{slugError}</p>
                )}
                {!slugError && !slugChecking && SLUG_REGEX.test(clubForm.slug) && (
                  <p className="mt-1 text-xs text-green-700">Available</p>
                )}
                <p className="mt-1 text-xs text-black/40">
                  Lowercase letters, numbers, and hyphens only · 3–60 characters
                </p>
              </div>

              <div className="md:col-span-2">
                <Input
                  label="Description"
                  value={clubForm.description}
                  onChange={(value) => updateClubField('description', value)}
                  placeholder="Tell runners what your club is about."
                  multiline
                />
              </div>

              <div className="md:col-span-2">
                <span className="mb-2 block text-sm font-medium text-black">Club address</span>
                <div className="flex flex-col gap-3 sm:flex-row">
                  <input
                    value={clubForm.location}
                    onChange={(e) => {
                      setAddressLookupMessage('');
                      setClubForm((prev) => ({
                        ...prev,
                        location: e.target.value,
                        latitude: '',
                        longitude: '',
                      }));
                    }}
                    placeholder="123 Main St, San Marcos, CA"
                    className="w-full rounded-2xl border border-black/10 bg-white px-4 py-3 text-sm text-black outline-none transition focus:border-black"
                  />

                  <button
                    type="button"
                    onClick={handleAddressLookup}
                    disabled={addressLookupLoading || !clubForm.location.trim()}
                    className="inline-flex min-h-[48px] shrink-0 items-center justify-center rounded-2xl bg-black px-5 py-3 text-sm font-medium text-white transition hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    {addressLookupLoading ? 'Confirming...' : 'Confirm address'}
                  </button>
                </div>

                <div className="mt-2 min-h-[20px]">
                  {addressLookupMessage ? (
                    <p className="text-sm text-green-700">{addressLookupMessage}</p>
                  ) : clubForm.latitude && clubForm.longitude ? (
                    <p className="text-xs text-black/45">
                      Coordinates ready: {clubForm.latitude}, {clubForm.longitude}
                    </p>
                  ) : (
                    <p className="text-xs text-black/45">
                      Confirm the address to save accurate map coordinates.
                    </p>
                  )}
                </div>
              </div>

              <ImageUploadField
                label="Club logo"
                value={clubForm.logo}
                previewHeightClass="h-40"
                uploading={logoUploading}
                onUpload={handleLogoUpload}
              />

              <ImageUploadField
                label="Hero image"
                value={clubForm.hero_image}
                previewHeightClass="h-40"
                uploading={heroUploading}
                onUpload={handleHeroUpload}
              />

              <Input
                label="Instagram handle"
                value={clubForm.instagram_handle}
                onChange={(value) => updateClubField('instagram_handle', value)}
                placeholder="@yourclub"
              />

              <Input
                label="Facebook link"
                value={clubForm.facebook_link}
                onChange={(value) => updateClubField('facebook_link', value)}
                placeholder="https://facebook.com/..."
              />

              <div className="md:col-span-2">
                <Input
                  label="Strava link"
                  value={clubForm.strava_link}
                  onChange={(value) => updateClubField('strava_link', value)}
                  placeholder="https://strava.com/clubs/..."
                />
              </div>
            </div>
          </section>

          <section className="rounded-[2rem] border border-black/10 bg-white p-6 shadow-sm md:p-8">
            <div className="flex items-center justify-between gap-4">
              <div>
                <h2 className="text-2xl font-semibold tracking-tight text-black">
                  Run schedule
                </h2>
                <p className="mt-2 text-sm text-black/55">
                  Add recurring weekly runs or one-off runs on a specific date.
                </p>
              </div>

              <button
                type="button"
                onClick={addRun}
                className="inline-flex rounded-2xl bg-black px-4 py-2.5 text-sm font-medium text-white transition hover:opacity-90"
              >
                Add run
              </button>
            </div>

            <div className="mt-6 space-y-5">
              {runs.map((run, index) => (
                <div
                  key={run.id || `new-run-${index}`}
                  className="rounded-[1.5rem] border border-black/10 bg-neutral-50 p-5"
                >
                  <div className="flex items-center justify-between gap-4">
                    <h3 className="text-lg font-semibold text-black">Run {index + 1}</h3>

                    <button
                      type="button"
                      onClick={() => removeRun(index)}
                      className="text-sm font-medium text-black/55 transition hover:text-black"
                    >
                      Remove
                    </button>
                  </div>

                  <div className="mt-5 grid gap-5 md:grid-cols-2">
                    {/* Run title — required, spans both columns */}
                    <div className="md:col-span-2">
                      <Input
                        label="Run title *"
                        value={run.title}
                        onChange={(value) => updateRun(index, { title: value })}
                        placeholder={`Run ${index + 1}`}
                      />
                    </div>

                    {/* Recurring / One-off toggle — spans both columns */}
                    <div className="md:col-span-2">
                      <span className="mb-2 block text-sm font-medium text-black">Run type</span>
                      <div className="inline-flex rounded-2xl border border-black/10 bg-white p-1">
                        <button
                          type="button"
                          onClick={() => updateRun(index, { is_recurring: true, run_date: '' })}
                          className={`rounded-xl px-4 py-2 text-sm font-medium transition ${
                            run.is_recurring
                              ? 'bg-black text-white shadow-sm'
                              : 'text-black/55 hover:text-black'
                          }`}
                        >
                          Recurring weekly
                        </button>
                        <button
                          type="button"
                          onClick={() => updateRun(index, { is_recurring: false, day_of_week: '' })}
                          className={`rounded-xl px-4 py-2 text-sm font-medium transition ${
                            !run.is_recurring
                              ? 'bg-black text-white shadow-sm'
                              : 'text-black/55 hover:text-black'
                          }`}
                        >
                          One-off date
                        </button>
                      </div>
                    </div>

                    {/* Day selector (recurring) OR date picker (one-off) */}
                    {run.is_recurring ? (
                      <label className="block">
                        <span className="mb-2 block text-sm font-medium text-black">Day</span>
                        <select
                          value={run.day_of_week}
                          onChange={(e) => updateRun(index, { day_of_week: e.target.value })}
                          className="w-full rounded-2xl border border-black/10 bg-white px-4 py-3 text-sm text-black outline-none transition focus:border-black"
                        >
                          {DAY_OPTIONS.map((day) => (
                            <option key={day} value={day}>{day}</option>
                          ))}
                        </select>
                      </label>
                    ) : (
                      <label className="block">
                        <span className="mb-2 block text-sm font-medium text-black">Date</span>
                        <input
                          type="date"
                          value={run.run_date}
                          onChange={(e) =>
                            updateRun(index, {
                              run_date: e.target.value,
                              day_of_week: dayOfWeekFromDate(e.target.value),
                            })
                          }
                          className="w-full rounded-2xl border border-black/10 bg-white px-4 py-3 text-sm text-black outline-none transition focus:border-black"
                        />
                        {run.run_date && (
                          <p className="mt-2 text-xs text-black/45">
                            Appears on {dayOfWeekFromDate(run.run_date)}{' '}
                            {new Date(run.run_date + 'T00:00:00').toLocaleDateString(undefined, {
                              month: 'long',
                              day: 'numeric',
                              year: 'numeric',
                            })}
                          </p>
                        )}
                      </label>
                    )}

                    <div className="block">
                      <span className="mb-2 block text-sm font-medium text-black">
                        Start time
                      </span>
                      <input
                        type="time"
                        value={run.start_time}
                        onChange={(e) => updateRun(index, { start_time: e.target.value })}
                        className="w-full rounded-2xl border border-black/10 bg-white px-4 py-3 text-sm text-black outline-none transition focus:border-black"
                      />
                      {run.start_time ? (
                        <p className="mt-2 text-xs text-black/45">
                          Displays as {formatTimeForDisplay(run.start_time)}
                        </p>
                      ) : null}
                    </div>

                    <div className="md:col-span-2">
                      <Input
                        label="Meet location"
                        value={run.location_address}
                        onChange={(value) =>
                          updateRun(index, { location_address: value })
                        }
                        placeholder="123 Main St, San Marcos, CA"
                      />
                    </div>

                    <Input
                      label="Distance"
                      value={run.distance}
                      onChange={(value) => updateRun(index, { distance: value })}
                      placeholder="3–5 miles"
                    />

                    <Input
                      label="Route URL"
                      value={run.route_url}
                      onChange={(value) => updateRun(index, { route_url: value })}
                      placeholder="https://strava.com/routes/..."
                    />

                    <div className="md:col-span-2">
                      <Input
                        label="Notes"
                        value={run.notes}
                        onChange={(value) => updateRun(index, { notes: value })}
                        placeholder="Any pacing notes, parking tips, or route context"
                        multiline
                      />
                    </div>

                    <div className="md:col-span-2">
                      <span className="mb-2 block text-sm font-medium text-black">Vibes</span>
                      <div className="flex flex-wrap gap-2">
                        {VIBE_OPTIONS.map((vibe) => {
                          const active = run.vibes.includes(vibe);

                          return (
                            <button
                              key={vibe}
                              type="button"
                              onClick={() => toggleRunVibe(index, vibe)}
                              className={`rounded-full border px-3 py-2 text-xs font-medium transition ${
                                active
                                  ? 'border-black bg-black text-white'
                                  : 'border-black/10 bg-white text-black/65 hover:border-black/20'
                              }`}
                            >
                              {vibe}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </section>

          <div className="flex flex-wrap gap-3">
            <button
              type="submit"
              disabled={saving}
              className="inline-flex rounded-2xl bg-black px-5 py-3 text-sm font-medium text-white transition hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-50"
            >
              {saving
                ? isEditMode
                  ? 'Saving changes...'
                  : 'Creating club...'
                : isEditMode
                  ? 'Save changes'
                  : 'Create club'}
            </button>

            <Link
              to={isEditMode && id ? `/clubs/${id}` : '/explore'}
              className="inline-flex rounded-2xl border border-black/10 bg-white px-5 py-3 text-sm font-medium text-black transition hover:bg-neutral-50"
            >
              Cancel
            </Link>
          </div>
        </form>
      </div>
    </div>
  );
}